import { Form, Col, FormControl, FormLabel, Row, FormSelect, FormCheck, Button, FormGroup } from "react-bootstrap";
import PageSelector from "../../components/common/PageSelector";
import { ILanguage, ISiteMenuItem } from "../../../models/Interfaces";
import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import ColourPicker from "../../components/common/ColourPicker";
import HTMLEditor from "../../components/common/HTMLEditor";
import { MenuAlignment } from "../../../config/Enums";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { Core, formValidation, notEmpty, callback } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import { CustomFvValidators } from "../../../config/CustomFVValidators";

interface IMenuItemEditor {
  selectedMenuItem: ISiteMenuItem,
  languages: Array<ILanguage>
  onClose: () => void;
  onSave: (menuitem: ISiteMenuItem) => void;
  asModal?: boolean;
}

const MenuItemEditor = (props: IMenuItemEditor) => {

  const [selectedMenuItem, setSelectedMenuItem] = useState<ISiteMenuItem | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const updateMenuProperty = (name: string, value: string | null | boolean) => {
    setSelectedMenuItem((prev) => {
      if (!prev) {
        return prev;
      }

      if (prev[name as keyof ISiteMenuItem] === value) {
        return prev;
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handlePageSelect = (selectedurl: string) : void => {
    updateMenuProperty("Url", selectedurl);
    if (fvRef.current) {
      fvRef.current.revalidateField("SelectedOrUrl");
    }
  };

  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
    }
  };

  const handleSave = async () : Promise<void> => {
    if (fvRef.current && selectedMenuItem && props.onSave) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        props.onSave(selectedMenuItem);
      }
    }
  };

  const getLanguageName = (shortcode: string) : string => {
    return props.languages?.find(l => l.ShortCode.toLowerCase() === shortcode.toLowerCase())?.Language ?? "ERROR - LANGUAGE UNKNOWN";
  };

  const createValidation = () : void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          SelectedOrUrl: {
            validators: {
              SiteUrl: { message: "Enter a valid URL, anchor, or leave blank" }
            }
          },
          Target: {
            validators: {
              notEmpty: { message: "Select target for menu click link" }
            }
          },
          DisplayText: {
            validators: {
              notEmpty: { message: "Display text for menu is required" }
            }
          },
          DisplayOrder: {
            validators: {
              notEmpty: { message: "Display order is required" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton(),
        }
      }).registerValidator("SiteUrl", CustomFvValidators.UrlValidator);
    }
  };

  useEffect(() => {
    if (formRef.current && !fvRef.current) {
      createValidation();
    }
  }, [selectedMenuItem]);

  useEffect(() => {
    if (props.selectedMenuItem) {
      setSelectedMenuItem(props.selectedMenuItem);
    }

    if (formRef.current && !fvRef.current) {
      createValidation();
    }

  }, [props.selectedMenuItem]);

  useEffect(() => {
    if (formRef.current && !fvRef.current) {
      createValidation();
    }

    return () => {
      if (fvRef.current) {
        fvRef.current.destroy();
        fvRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {selectedMenuItem && (
        <div id={`menuitem-${props.selectedMenuItem.Id}`} key="MENUITEMTEMPLATE" className={!props.asModal ? "border" : ""}>

          {!props.asModal && (
            <div className="panel__header white text-center">
              <span><b>Menu Item Details</b></span>
            </div>
          )}

          <Form id="MenuItemForm" ref={formRef} className="ps-3">
            <Row>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Select a page (or add external URL)</FormLabel>
                <PageSelector
                  id="SelectedOrUrl"
                  name="SelectedOrUrl"
                  onPageClear={() => updateMenuProperty("Url", "")}
                  onPageSelect={handlePageSelect}
                  existingUrl={selectedMenuItem.Url ?? ""}
                  closeOnSelect={true}
                />
              </Col>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Target (open selected url in new tab/page or same page/tab)</FormLabel>
                <FormSelect
                  name="Target"
                  id="Target"
                  value={selectedMenuItem.Target ?? ""}
                  onChange={(e) => updateMenuProperty(e.target.name, e.target.value)}>
                  <option disabled value="">Select url target...</option>
                  <option value="_self">Same Tab</option>
                  <option value="_blank">New Tab/Window</option>
                  <option value="_parent">Parent Window</option>
                  <option value="_top">Top Window</option>
                </FormSelect>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <hr />
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Display Text</FormLabel>
                <FormControl
                  type="text"
                  name="DisplayText"
                  id="DisplayText"
                  autoComplete="off"
                  placeholder="menu item text..."
                  value={selectedMenuItem.DisplayText ?? ""}
                  onChange={(e) => updateMenuProperty(e.target.name, e.target.value)}
                />
              </Col>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Page Language</FormLabel>
                <FormControl
                  type="text"
                  readOnly={true}
                  disabled={true}
                  value={getLanguageName(selectedMenuItem.Language ?? "")}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={4} className="mt-3">
                <FormLabel>Background Colour</FormLabel>
                <ColourPicker onColourChange={(colour) => updateMenuProperty("BackgroundColour", colour ?? "")} selectedColour={selectedMenuItem.BackgroundColour ?? ""} />
              </Col>
              <Col md={12} xl={4} className="mt-3">
                <FormLabel>Text Colour</FormLabel>
                <ColourPicker onColourChange={(colour) => updateMenuProperty("TextColour", colour ?? "")} selectedColour={selectedMenuItem.TextColour ?? ""} />
              </Col>
              <Col md={12} xl={4} className="mt-3">
                <FormLabel>Highlight Colour</FormLabel>
                <ColourPicker onColourChange={(colour) => updateMenuProperty("HighlightColour", colour ?? "")} selectedColour={selectedMenuItem.HighlightColour ?? ""} />
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={4} className="mt-3">
                <FormLabel>Display Position</FormLabel>
                <FormControl
                  name="DisplayOrder"
                  id="DisplayOrder"
                  type="number"
                  step="1"
                  placeholder="display position..."
                  value={selectedMenuItem.DisplayOrder ?? ""}
                  onChange={(e) => updateMenuProperty(e.target.name, e.target.value)} />
              </Col>
              <Col md={12} xl={4} className="mt-3">
                <FormLabel>Menu Item Class</FormLabel>
                <FormControl
                  name="MenuItemClass"
                  type="text"
                  placeholder="menu item class..."
                  value={selectedMenuItem.MenuItemClass ?? ""}
                  onChange={(e) => updateMenuProperty(e.target.name, e.target.value)} />
              </Col>
              <Col md={12} xl={4} className="mt-3">
                <FormLabel>Enabled</FormLabel>
                <FormCheck
                  className="form-check btn-nomin"
                  type="checkbox"
                  name="Enabled"
                  id="Enabled"
                  checked={selectedMenuItem.Enabled ?? false}
                  onChange={(e) => updateMenuProperty(e.target.name, e.target.checked)} />
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={10} className="mt-3">
                <FormLabel>Menu Header Column (optional)</FormLabel>
                <HTMLEditor documentKey={selectedMenuItem.Id} value={selectedMenuItem.StyledColumn ?? ""}
                  onChange={content => updateMenuProperty("StyledColumn", content)} />
              </Col>
              <Col md={12} xl={2} className="mt-3">
                <FormLabel>Align</FormLabel>
                <FormSelect
                  name="HeaderColAlign"
                  value={selectedMenuItem.HeaderColAlign ?? ""}
                  onChange={(e) => updateMenuProperty(e.target.name, e.target.value)}>
                  <option disabled value="">Select header column alignment...</option>
                  {
                    Object.keys(MenuAlignment).map(mak =>
                      <option key={`option-${mak}`} value={mak}>{MenuAlignment[mak as keyof typeof MenuAlignment]}</option>
                    )
                  }
                </FormSelect>
              </Col>
            </Row>
            <Row>
              <Col sm={12}>
                <hr />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={12} className="text-end">
                <Button variant="outline-success" title="save menu item" className="me-1" onClick={handleSave}>
                  <FontAwesomeIcon icon={faSave} />&nbsp;Save
                </Button>
                <Button variant="outline-primary" title="close" onClick={handleClose}>
                  Close
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </>
  );
}

export default MenuItemEditor;
